import {match} from 'ts-pattern';

import {TemplateResponseBody, useAddTemplateMutation} from '@dms/api/core';
import {handleApiError} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {MAX_CREATE_TEMPLATE_COPY_ATTEMPTS} from '../constants/MAX_CREATE_TEMPLATE_COPY_ATTEMPTS';
import {getDocumentTemplateEditorPath} from '../utils/getDocumentTemplateEditorPath';
import {getTemplateCopyTitle} from '../utils/getTemplateCopyTitle';
import {isDuplicateTemplateTitleError} from '../utils/isDuplicateTemplateTitleError';

export const useCreateTemplateCopy = () => {
  const [addTemplate, {isLoading}] = useAddTemplateMutation();
  const navigate = useNavigate();

  const createTemplateCopy = async (
    template: TemplateResponseBody,
    source: string,
    siblingTitles: string[]
  ) => {
    const existingTitles = new Set(siblingTitles);
    let title = getTemplateCopyTitle(template.title);

    while (existingTitles.has(title)) {
      title = getTemplateCopyTitle(title);
    }

    for (let attempt = 0; attempt < MAX_CREATE_TEMPLATE_COPY_ATTEMPTS; attempt += 1) {
      const shouldRetry = await addTemplate({
        addTemplateRequestBody: {
          authorizationProfileId: template.authorizationProfileId,
          documentCode: template.documentCode,
          fileId: null,
          locale: template.locale ?? null,
          note: template.note,
          template: source,
          title,
        },
      })
        .unwrap()
        .then((data) => {
          navigate(getDocumentTemplateEditorPath(data.id));
          return false;
        })
        .catch((error: ApiError) =>
          match(
            isDuplicateTemplateTitleError(error) && attempt + 1 < MAX_CREATE_TEMPLATE_COPY_ATTEMPTS
          )
            .with(true, () => true)
            .otherwise(() => {
              handleApiError(error);
              return false;
            })
        );

      if (!shouldRetry) {
        return;
      }

      do {
        title = getTemplateCopyTitle(title);
      } while (existingTitles.has(title));
    }
  };

  return {createTemplateCopy, isLoading};
};
